/**
 * Search Analytics - Tracks search behavior for relevance tuning
 *
 * Collects anonymous metrics to improve search quality:
 * - Click-through rate (which results get clicked)
 * - Zero-result queries (queries that return no results)
 * - Query patterns (common search terms and intents)
 * - Result position bias (do users click top results more?)
 */

export interface SearchEvent {
  type: 'search' | 'click' | 'zero_results' | 'refinement';
  query: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface SearchClickEvent extends SearchEvent {
  type: 'click';
  metadata: {
    resultType: 'movie' | 'watchlist' | 'user' | 'person';
    resultId: string;
    position: number;
    totalResults: number;
  };
}

export interface SearchAnalyticsSummary {
  totalSearches: number;
  uniqueQueries: number;
  zeroResultQueries: string[];
  popularQueries: Array<{ query: string; count: number }>;
  clickThroughRate: number;
  avgClickPosition: number;
}

const MAX_EVENTS = 100;
const MAX_ZERO_RESULT_QUERIES = 20;

class SearchAnalyticsService {
  private events: SearchEvent[] = [];
  private zeroResultQueries: Set<string> = new Set();
  private queryFrequency: Map<string, number> = new Map();
  private clickPositions: number[] = [];
  private searchCount: number = 0;
  private clickCount: number = 0;

  /**
   * Track a search event
   */
  trackSearch(query: string, resultCount: number, breakdown?: {
    movies: number;
    watchlists: number;
    users: number;
    people: number;
  }): void {
    const normalizedQuery = query.toLowerCase().trim();
    this.searchCount++;

    // Track query frequency
    const currentCount = this.queryFrequency.get(normalizedQuery) || 0;
    this.queryFrequency.set(normalizedQuery, currentCount + 1);

    // Track zero-result queries
    if (resultCount === 0) {
      this.zeroResultQueries.add(normalizedQuery);
      // Keep only recent zero-result queries
      if (this.zeroResultQueries.size > MAX_ZERO_RESULT_QUERIES) {
        const first = this.zeroResultQueries.values().next().value;
        if (first) this.zeroResultQueries.delete(first);
      }

      this.addEvent({
        type: 'zero_results',
        query: normalizedQuery,
        timestamp: Date.now(),
        metadata: { breakdown }
      });
    } else {
      this.addEvent({
        type: 'search',
        query: normalizedQuery,
        timestamp: Date.now(),
        metadata: { resultCount, breakdown }
      });
    }

    console.log('📊 SearchAnalytics: Tracked search', {
      query: normalizedQuery,
      resultCount,
      isZeroResult: resultCount === 0
    });
  }

  /**
   * Track a click on a search result
   */
  trackClick(
    query: string,
    resultType: 'movie' | 'watchlist' | 'user' | 'person',
    resultId: string,
    position: number,
    totalResults: number
  ): void {
    this.clickCount++;
    this.clickPositions.push(position);

    const event: SearchClickEvent = {
      type: 'click',
      query: query.toLowerCase().trim(),
      timestamp: Date.now(),
      metadata: {
        resultType,
        resultId,
        position,
        totalResults
      }
    };

    this.addEvent(event);

    console.log('📊 SearchAnalytics: Tracked click', {
      query,
      resultType,
      position,
      ctr: this.getClickThroughRate().toFixed(2)
    });
  }

  /**
   * Track query refinement (user modifies search)
   */
  trackRefinement(originalQuery: string, refinedQuery: string): void {
    this.addEvent({
      type: 'refinement',
      query: originalQuery.toLowerCase().trim(),
      timestamp: Date.now(),
      metadata: { refinedTo: refinedQuery.toLowerCase().trim() }
    });
  }

  /**
   * Get click-through rate
   */
  getClickThroughRate(): number {
    if (this.searchCount === 0) return 0;
    return this.clickCount / this.searchCount;
  }

  /**
   * Get average click position (lower is better - users click top results)
   */
  getAvgClickPosition(): number {
    if (this.clickPositions.length === 0) return 0;
    const sum = this.clickPositions.reduce((a, b) => a + b, 0);
    return sum / this.clickPositions.length;
  }

  /**
   * Get popular queries (sorted by frequency)
   */
  getPopularQueries(limit: number = 10): Array<{ query: string; count: number }> {
    return Array.from(this.queryFrequency.entries())
      .map(([query, count]) => ({ query, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Get zero-result queries (queries that need attention)
   */
  getZeroResultQueries(): string[] {
    return Array.from(this.zeroResultQueries);
  }

  /**
   * Get analytics summary
   */
  getSummary(): SearchAnalyticsSummary {
    return {
      totalSearches: this.searchCount,
      uniqueQueries: this.queryFrequency.size,
      zeroResultQueries: this.getZeroResultQueries(),
      popularQueries: this.getPopularQueries(5),
      clickThroughRate: this.getClickThroughRate(),
      avgClickPosition: this.getAvgClickPosition()
    };
  }

  /**
   * Get recent events for debugging
   */
  getRecentEvents(limit: number = 20): SearchEvent[] {
    return this.events.slice(-limit);
  }

  /**
   * Clear all analytics data
   */
  clear(): void {
    this.events = [];
    this.zeroResultQueries.clear();
    this.queryFrequency.clear();
    this.clickPositions = [];
    this.searchCount = 0;
    this.clickCount = 0;
  }

  /**
   * Export analytics data for external processing
   */
  export(): {
    events: SearchEvent[];
    summary: SearchAnalyticsSummary;
  } {
    return {
      events: [...this.events],
      summary: this.getSummary()
    };
  }

  private addEvent(event: SearchEvent): void {
    this.events.push(event);
    // Keep only recent events
    if (this.events.length > MAX_EVENTS) {
      this.events.shift();
    }
  }
}

// Export singleton instance
export const searchAnalytics = new SearchAnalyticsService();
